import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { CartContextValue, CartItem } from '@/features/shop/types'

const STORAGE_KEY = 'odd-shop-cart'

export const CartContext = createContext<CartContextValue | null>(null)

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as CartItem[]) : []
  } catch {
    return []
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // ignore storage errors
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart)
  const [isOpen, setIsOpen] = useState(false)

  // Sync to localStorage on every change
  useEffect(() => {
    saveCart(items)
  }, [items])

  const addItem = useCallback((incoming: CartItem) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.productId === incoming.productId && i.variantId === incoming.variantId
      )
      if (idx >= 0) {
        const updated = [...prev]
        const existing = updated[idx]!
        updated[idx] = { ...existing, quantity: existing.quantity + incoming.quantity }
        return updated
      }
      return [...prev, incoming]
    })
    setIsOpen(true)
  }, [])

  const removeItem = useCallback((productId: string, variantId: number) => {
    setItems((prev) =>
      prev.filter((i) => !(i.productId === productId && i.variantId === variantId))
    )
  }, [])

  const updateQuantity = useCallback((productId: string, variantId: number, qty: number) => {
    if (qty <= 0) {
      setItems((prev) =>
        prev.filter((i) => !(i.productId === productId && i.variantId === variantId))
      )
    } else {
      setItems((prev) =>
        prev.map((i) =>
          i.productId === productId && i.variantId === variantId ? { ...i, quantity: qty } : i
        )
      )
    }
  }, [])

  const clearCart = useCallback(() => setItems([]), [])
  const openCart = useCallback(() => setIsOpen(true), [])
  const closeCart = useCallback(() => setIsOpen(false), [])

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items])
  const subtotalCents = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  )

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      isOpen,
      itemCount,
      subtotalCents,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      openCart,
      closeCart,
    }),
    [items, isOpen, itemCount, subtotalCents, addItem, removeItem, updateQuantity, clearCart, openCart, closeCart]
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}
